import type { ReactNode } from 'react'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { cn } from '@/helpers/utils'
import { type TKey, useTranslation } from '@/translations/i18n'

export interface TeamFilterOption<T extends string> {
	id: T
	label: TKey
}

interface TeamFilterSelectProps<T extends string> {
	/** Read by screen readers; the trigger itself shows the selected value. */
	label: TKey
	options: readonly TeamFilterOption<T>[]
	value: T
	onChange: (value: T) => void
	/** Glyph before the value (the Locations filter's sliders). */
	icon?: ReactNode
	className?: string
}

/**
 * One facet of the Team filter bar: a single-select whose first option is the
 * "All …" value, so the trigger always reads as the current answer ("All
 * roles", "Creator"). Unlike the roster's multi-select facets, the design
 * shows the selection on the trigger, which a single value fits.
 *
 * The roster is unpaginated and filtered in the browser, so picking a value
 * costs no request.
 */
export function TeamFilterSelect<T extends string>({
	label,
	options,
	value,
	onChange,
	icon,
	className
}: TeamFilterSelectProps<T>) {
	const { t } = useTranslation()
	const selected = options.find((option) => option.id === value)

	return (
		<Select
			onValueChange={(next) => {
				if (next !== null) {
					onChange(next)
				}
			}}
			value={value}
		>
			<SelectTrigger
				aria-label={t(label)}
				// `min-w-0` lets the trigger shrink in a tight toolbar; the value
				// then truncates and the chevron (already `shrink-0`) stays.
				className={cn(
					'h-10 min-w-0 max-w-full gap-2 border-grey-400 bg-neutral-white px-3 font-medium text-neutral-900 text-sm shadow-xs [&_svg]:size-5',
					className
				)}
				size='sm'
			>
				{icon}
				<SelectValue className='min-w-0'>
					<span className='truncate whitespace-nowrap'>
						{selected ? t(selected.label) : null}
					</span>
				</SelectValue>
			</SelectTrigger>
			<SelectContent className='min-w-50'>
				{options.map((option) => (
					<SelectItem key={option.id} value={option.id}>
						{t(option.label)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}
